import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import TickMark from "../../../components/tickmark";
import immediateOnDemandRealtime from "../../../services/immediateOnDemandRealtime";
import {
  formatAppointmentNumber,
  formatAppointmentSlotLabel,
  getAppointmentId,
} from "./matchingUtils";
import "./immediateOnDemand.css";

export const WAITING_ROOM_COUNTDOWN_SECONDS = 30;
const MEETING_LINK_POLL_MS = 5000;
const MEETING_LINK_MAX_POLLS = 24; // ~2 minutes

const SUPPORT_URL = process.env.REACT_APP_SUPPORT_URL || "";
const COPY_MESSAGE_MS = 5000;

const svgProps = {
  viewBox: "0 0 24 24",
  width: 15,
  height: 15,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};
const MailIcon = () => (
  <svg {...svgProps}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
);
const CalendarIcon = () => (
  <svg {...svgProps}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>
);
const HelpIcon = () => (
  <svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14M12 17.5v.01" /></svg>
);
const CheckIcon = () => (
  <svg {...svgProps}><path d="m5 12 5 5 9-10" /></svg>
);

const getMediumEmail = (matchData) =>
  (matchData &&
    (matchData.providerEmail ||
      matchData.provider_email ||
      matchData.mediumEmail ||
      matchData.medium_email)) ||
  "";

// Clipboard API needs a secure context; fall back to execCommand otherwise.
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // fall through to the legacy path
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    return false;
  }
};

const formatWaitingRoomCountdown = (seconds) => {
  const safeSeconds = Math.max(seconds, 0);
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, "0")}`;
};

const getFirstMatchingValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getMatchedPartnerName = (matchData, participantRole) => {
  if (!matchData) {
    return "";
  }

  const clientName = getFirstMatchingValue(
    matchData.clientName,
    matchData.client_name,
    matchData.userScreenName,
    matchData.user_screen_name
  );
  const mediumName = getFirstMatchingValue(
    matchData.providerName,
    matchData.provider_name,
    matchData.mediumName,
    matchData.medium_name,
    matchData.mediumScreenName,
    matchData.medium_screen_name
  );

  return participantRole === "provider" ? clientName : mediumName;
};

export default function ImmediateOnDemandThankYou({
  matchData,
  participantRole = "client",
  onDone,
  embedded = false,
  // Opt-in: only the /go-on-demand-immediate-session route turns this on.
  showWaitingRoom: waitingRoomRequested = false,
}) {
  const { t } = useTranslation("clientLive");
  const appointmentNumber = formatAppointmentNumber(matchData);
  const partnerName =
    getMatchedPartnerName(matchData, participantRole) || t("thankYou.partnerFallback");
  const appointmentSlotLabel = formatAppointmentSlotLabel(matchData);
  const appointmentId = getAppointmentId(matchData);
  const showWaitingRoom =
    waitingRoomRequested && participantRole === "client" && Boolean(appointmentId);
  const mediumName =
    getMatchedPartnerName(matchData, participantRole) || t("thankYou.mediumFallback");

  const [secondsLeft, setSecondsLeft] = useState(WAITING_ROOM_COUNTDOWN_SECONDS);
  const [meetingLink, setMeetingLink] = useState("");
  // Medium's email from get-appointment-zoom-link (medium_email), used when the
  // match payload doesn't include it.
  const [fetchedMediumEmail, setFetchedMediumEmail] = useState("");
  const countdownDone = secondsLeft <= 0;

  // 30s countdown shown under the confirmation.
  useEffect(() => {
    if (!showWaitingRoom || countdownDone) return undefined;

    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [showWaitingRoom, countdownDone, secondsLeft]);

  // Listen for ZoomLinkUpdate over Pusher, and check once up front in case
  // the CreateZoomLink job finished before this screen subscribed.
  useEffect(() => {
    if (!showWaitingRoom) return undefined;

    let active = true;
    const unsubscribe = immediateOnDemandRealtime.subscribeZoomLink(
      appointmentId,
      (link) => active && setMeetingLink(link)
    );

    immediateOnDemandRealtime
      .getAppointmentZoomDetails(appointmentId)
      .then(({ meetingLink: link, mediumEmail }) => {
        if (!active) return;
        if (mediumEmail) setFetchedMediumEmail(mediumEmail);
        if (link) setMeetingLink((current) => current || link);
      })
      .catch(() => {});

    return () => {
      active = false;
      unsubscribe();
    };
  }, [showWaitingRoom, appointmentId]);

  // Safety net: if the Pusher event hasn't arrived, poll the API until the
  // link shows up so "Click here to enter" becomes clickable.
  useEffect(() => {
    if (!showWaitingRoom || meetingLink) return undefined;

    let active = true;
    let polls = 0;
    const poll = () => {
      polls += 1;
      immediateOnDemandRealtime
        .getAppointmentZoomDetails(appointmentId)
        .then(({ meetingLink: link, mediumEmail }) => {
          if (!active) return;
          if (mediumEmail) setFetchedMediumEmail(mediumEmail);
          if (link) setMeetingLink(link);
        })
        .catch(() => {});
      if (polls >= MEETING_LINK_MAX_POLLS) clearInterval(timer);
    };
    const timer = setInterval(poll, MEETING_LINK_POLL_MS);
    poll();

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [showWaitingRoom, meetingLink, appointmentId]);

  const [emailCopied, setEmailCopied] = useState(false);
  const [helpMessage, setHelpMessage] = useState(null);
  const helpMessageTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(helpMessageTimerRef.current), []);

  const showHelpMessage = (message, copied = false) => {
    clearTimeout(helpMessageTimerRef.current);
    setHelpMessage(message);
    setEmailCopied(copied);
    helpMessageTimerRef.current = setTimeout(() => {
      setHelpMessage(null);
      setEmailCopied(false);
    }, COPY_MESSAGE_MS);
  };

  // "Contact My Medium": copy the Medium's email, confirm on screen, and open
  // the client's email app with it filled in.
  const contactMedium = async () => {
    let email = getMediumEmail(matchData) || fetchedMediumEmail;

    if (!email && appointmentId) {
      try {
        const details = await immediateOnDemandRealtime.getAppointmentZoomDetails(appointmentId);
        email = details.mediumEmail;
        if (email) setFetchedMediumEmail(email);
      } catch (error) {
        // handled below as "couldn't find"
      }
    }

    if (!email) {
      showHelpMessage({
        type: "error",
        content: t("thankYou.help.emailNotFound"),
      });
      return;
    }

    const copied = await copyToClipboard(email);
    showHelpMessage(
      {
        type: "success",
        content: copied ? (
          <>
            <Trans
              i18nKey="thankYou.help.emailCopied"
              ns="clientLive"
              values={{ name: mediumName, email }}
              components={{ b: <b /> }}
            />
          </>
        ) : (
          <>
            <Trans
              i18nKey="thankYou.help.emailIs"
              ns="clientLive"
              values={{ name: mediumName, email }}
              components={{ b: <b /> }}
            />
          </>
        ),
      },
      copied
    );

    const subject = appointmentNumber
      ? `Immediate session ${appointmentNumber}`
      : "Immediate session";
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
  };

  return (

    <div className={`immediate-ondemand-page${embedded ? " immediate-ondemand-page--embedded" : ""}`}>
      <div className="immediate-ondemand-thankyou">
        {/* <TickMark /> */}
        <img src="../images/new-cll.png" alt="user" />
        <h3 className="mt-3">
                    {t("thankYou.title")}
                    {/* {userDetails && userDetails.first_name}, your appointment is
                      confirmed */}
                  </h3>

        {/* On /go-on-demand-immediate-session (waiting room shown) these lines are
            hidden; everywhere else (Medium side, My Appointments) they stay. */}
        {!showWaitingRoom ? (
          <>
                  <div className="font-size-17 advance-guest-book-p">
                    {t("thankYou.checkEmail")}
                  </div>
        <h5>
          <Trans
            i18nKey={appointmentNumber ? "thankYou.bookedWithNumber" : "thankYou.booked"}
            ns="clientLive"
            values={{ appointmentNumber, partnerName }}
            components={{ b: <b /> }}
          />
          {/* {participantRole == "client" && "Meet medium in 5 mins." } */}
        </h5>
        {appointmentSlotLabel ? (
          <div className="immediate-ondemand-slot">
            <Trans
              i18nKey="thankYou.scheduledFor"
              ns="clientLive"
              values={{ slot: appointmentSlotLabel }}
              components={{ b: <b /> }}
            />
          </div>
        ) : null}
          </>
        ) : null}
        {showWaitingRoom ? (
          <div className="immediate-ondemand-waiting-room">
            <p className="immediate-ondemand-waiting-room-text">
              {meetingLink ? (
                <a
                  href={meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="immediate-ondemand-waiting-room-link"
                >
                  {t("thankYou.waitingRoom.enter")}
                </a>
              ) : (
                <a
                  href={"#"}
                 
                  rel="noopener noreferrer"
                  className="immediate-ondemand-waiting-room-link"
                >
                  {t("thankYou.waitingRoom.enter")}
                </a>
              )}{" "}
              <Trans
                i18nKey="thankYou.waitingRoom.text"
                ns="clientLive"
                values={{ name: mediumName, time: formatWaitingRoomCountdown(secondsLeft) }}
                components={{ b: <b />, timer: <b className="immediate-ondemand-waiting-room-timer" /> }}
              />
            </p>
            <div className="immediate-ondemand-help">
              <div className="immediate-ondemand-help-title">{t("thankYou.help.title")}</div>
              <div className="immediate-ondemand-help-options">
                <button
                  type="button"
                  className={`immediate-ondemand-help-option${
                    emailCopied ? " immediate-ondemand-help-option--done" : ""
                  }`}
                  onClick={contactMedium}
                >
                  {emailCopied ? <CheckIcon /> : <MailIcon />}
                  {emailCopied ? t("thankYou.help.emailCopiedButton") : t("thankYou.help.contactMedium")}
                </button>
                <Link to="/appointments" className="immediate-ondemand-help-option">
                  <CalendarIcon />
                  {t("thankYou.goToAppointments")}
                </Link>
                {SUPPORT_URL ? (
                  <a
                    href={SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="immediate-ondemand-help-option"
                  >
                    <HelpIcon />
                    {t("thankYou.help.contactSupport")}
                  </a>
                ) : null}
              </div>
              {helpMessage ? (
                <div
                  className={`immediate-ondemand-help-message${
                    helpMessage.type === "error" ? " immediate-ondemand-help-message--error" : ""
                  }`}
                  role="status"
                >
                  {helpMessage.type === "error" ? null : <CheckIcon />}
                  <span>{helpMessage.content}</span>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {onDone ? (
          <button
            type="button"
            className="immediate-ondemand-primary"
            onClick={onDone}
          >
            {t("thankYou.goToAppointments")}
          </button>
        ) : null}
      </div>
    </div>
  );
}
